(async () => {
  if (!customElements.get('how-to-use')) {
    const {
      default: Swiper,
      Navigation: Navigation,
      Pagination: Pagination,
      EffectFade: EffectFade,
      Controller: Controller
    } = await import("./swiper.esm-8fb044b1.async.js");

    class HowToUseTabs extends HTMLElement {
      constructor() {
        super();

        this.tabsContainer = this.querySelector('.tabs-container');
        this.tabsList = this.tabsContainer.querySelector('ul');
        this.tabButtons = this.tabsList.querySelectorAll('a');
        this.tabPanels = this.querySelectorAll('.tab__panels > div');

        if (this.tabButtons.length == 0 || this.tabPanels.length === 0) {
          return;
        }

        this.init();
      }

      connectedCallback() {
        this.howToUseElement = this.querySelector('.tab__panels > div[id$="how-to-use"]');
        this.communityElement = this.querySelector('.tab__panels > div[id$="community"] .swiper');
        this.ingredientsElement = this.querySelector('.tab__panels > div[id$="ingredients"] .swiper');

        this.initSwipers();
      }

      initSwipers() {
        this.initHowToUseSwiper();
        this.initCommunitySwiper();
        this.initIngredientsSwiper();
      }

      initHowToUseSwiper() {
        if (!this.howToUseElement) {
          return;
        }

        const howToUseSwiperSecondaryContainer = this.howToUseElement.querySelector('[data-swiper-how-to-user-secondary]');
        const howToUseSwiperMainContainer = this.howToUseElement.querySelector('[data-swiper-how-to-use-main]');

        const recalcOffset = function(swiper) {
          const slide = swiper.slides[0];

          if (!slide) {
            return;
          }

          const slideWidth = slide.getBoundingClientRect().width;
          const viewportWidth = swiper.width;
          const spaceBetween = swiper.params.spaceBetween || 0;
          const offset = viewportWidth - (slideWidth + spaceBetween) + 1;

          swiper.params.slidesOffsetAfter = offset > 0 ? offset : 0;

          swiper.update();
        }

        const secondaryOptions = {
          slidesPerView: 1,
          centeredSlides: true,
          effect: 'fade',
          fadeEffect: {
            crossFade: true
          },
          loop: false,
          direction: "horizontal",
          spaceBetween: 0,
          allowTouchMove: false,
          autoHeight: true,
          modules: [Navigation, Pagination, EffectFade, Controller]
        };

        const mainOptions = {
          slidesPerView: 1.2,
          centeredSlides: false,
          centeredSlidesBounds: false,
          loop: false,
          autoplay: false,
          speed: 400,
          direction: "horizontal",
          spaceBetween: 8,
          longSwipes: false,
          longSwipersRatio: 0,
          touchRatio: 1,
          watchOverflow: true,
          centerInsufficientSlides: true,
          slidesOffsetAfter: 1000, /* initial */
          breakpoints: {
            510: {
              slidesPerView: 2.2
            },
            768: {
              slidesPerView: 3.2
            }
          },
          on: {
            init() {
              recalcOffset(this);
            },
            resize() {
              recalcOffset(this);
            },
            slideChangeTransitionEnd: () => {
              for (const slide of howToUseSwiperMainContainer.querySelectorAll('.swiper-slide:not(.swiper-slide-active)')) {
                const video = slide.querySelector('video');

                if (video === null) {
                  continue;
                }

                video.pause();
              }

              const activeSlide = howToUseSwiperMainContainer.querySelector('.swiper-slide.swiper-slide-active');
              const activeVideo = activeSlide?.querySelector('video');

              if (activeSlide && activeVideo) {
                activeVideo.play();
              }
            }
          },
          modules: [Controller]
        };

        this.howToUseSwiperSecondaryInstance = new Swiper(howToUseSwiperSecondaryContainer, secondaryOptions);
        this.howToUseSwiperMainInstance = new Swiper(howToUseSwiperMainContainer, mainOptions);

        this.howToUseSwiperMainInstance.controller.control = this.howToUseSwiperSecondaryInstance;

        this.howToUseSwiperMainInstance.slides.forEach((slide, index) => {
          slide.addEventListener('click', () => {
            this.howToUseSwiperMainInstance.slideTo(index);
          });
        });
      }

      initCommunitySwiper() {
        if (!this.communityElement) {
          return;
        }

        const swiperOptions = {
          slidesPerView: 1.2,
          loop: false,
          spaceBetween: 8,
          breakpoints: {
            510: {
              slidesPerView: 2.2
            },
            768: {
              slidesPerView: 3
            }
          },
          on: {
            slideChangeTransitionEnd: () => {
              for (const slide of this.communityElement.querySelectorAll('.swiper-slide:not(.swiper-slide-active)')) {
                const video = slide.querySelector('video');

                if (video === null) {
                  continue;
                }

                video.pause();
              }

              const activeSlide = this.communityElement.querySelector('.swiper-slide.swiper-slide-active');
              const activeVideo = activeSlide?.querySelector('video');

              if (activeSlide && activeVideo) {
                activeVideo.play();
              }
            }
          }
        }

        this.communitySwiperInstance = new Swiper(this.communityElement, swiperOptions);
      }

      initIngredientsSwiper() {
        if (!this.ingredientsElement) {
          return;
        }

        const swiperOptions = {
          slidesPerView: 1.2,
          loop: false,
          spaceBetween: 8,
          breakpoints: {
            510: {
              slidesPerView: 2.2
            },
            768: {
              slidesPerView: 3
            }
          },
          on: {
            slideChangeTransitionEnd: () => {
              for (const slide of this.ingredientsElement.querySelectorAll('.swiper-slide:not(.swiper-slide-active)')) {
                const video = slide.querySelector('video');

                if (video === null) {
                  continue;
                }

                video.pause();
              }

              const activeSlide = this.ingredientsElement.querySelector('.swiper-slide.swiper-slide-active');
              const activeVideo = activeSlide?.querySelector('video');

              if (activeSlide && activeVideo) {
                activeVideo.play();
              }
            }
          }
        }

        this.ingredientsSwiperInstance = new Swiper(this.ingredientsElement, swiperOptions);
      }

      init() {
        this.tabsList.setAttribute('role', 'tablist');

        this.tabsList.querySelectorAll('li').forEach(li => { 
          li.setAttribute('role', 'presentation');
        });

        this.tabButtons.forEach((button, index) => {
          button.setAttribute('role', 'tab');

          if (index === 0) {
            button.setAttribute('aria-selected', 'true');
          } else {
            button.setAttribute('tabindex', '-1');
            this.tabPanels[index].setAttribute('hidden', '');
          }
        });

        this.tabPanels.forEach(panel => {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('tabindex', '0');
        });

        this.tabsContainer.addEventListener('click', event => {
          const clickedTab = event.target.closest('a');

          if (!clickedTab) return;

          event.preventDefault();

          this.activateTab(clickedTab);
        });

        this.tabsContainer.addEventListener('keydown', event => {
          switch(event.key) {
            case 'ArrowLeft':
              event.preventDefault();
              this.moveLeft();
              break;
            case 'ArrowRight':
              event.preventDefault();
              this.moveRight();
              break;
            case 'Home':
              event.preventDefault();
              this.activateTab(this.tabButtons[0]);
              break;
            case 'End':
              event.preventDefault();
              this.activateTab(this.tabButtons[this.tabButtons.length - 1]);
              break;
          }
        });
      }

      moveLeft() {
        const activeTab = this.tabsList.querySelector('[aria-selected="true"]');
        const previousTab = activeTab.parentNode.previousElementSibling;

        if (!previousTab) {
          this.activateTab(this.tabButtons[this.tabButtons.length - 1]);
        } else {
          this.activateTab(previousTab.querySelector('a'));
        }
      }

      moveRight() {
        const activeTab = this.tabsList.querySelector('[aria-selected="true"]');
        const nextTab = activeTab.parentNode.nextElementSibling;

        if (!nextTab) {
          this.activateTab(this.tabButtons[0]);
        } else {
          this.activateTab(nextTab.querySelector('a'));
        }
      }

      activateTab(clickedTab) {
        const activePanel = this.querySelector(clickedTab.getAttribute('href'));

        this.tabButtons.forEach(button => {
          button.setAttribute('aria-selected', 'false');
          button.classList.remove('button--primary', 'button--active');
          button.classList.add('button--secondary');
          button.setAttribute('tabindex', '-1');
        });

        this.tabPanels.forEach(panel => {
          panel.setAttribute('hidden', '');
        });

        activePanel.removeAttribute('hidden');

        clickedTab.setAttribute('aria-selected', 'true');
        clickedTab.setAttribute('tabindex', '0');
        activePanel.focus();
      }
    }

    customElements.define('how-to-use-tabs', HowToUseTabs);
  }
})();